/**
 * Labstep
 *
 * @module components/Layout/Menu/TopBar/Breadcrumb
 * @desc Breadcrumb
 */

import { Context as GroupContext } from 'labstep-web/components/Layout/Menu/TopBar/Breadcrumb/Context/Group';
import { ModuleContext } from 'labstep-web/components/Layout/Menu/TopBar/Breadcrumb/Context/Module';
import { OrganizationContext } from 'labstep-web/components/Layout/Menu/TopBar/Breadcrumb/Context/Organization';
import { LayoutMenuTopbarBreadcrumbTitle as Title } from 'labstep-web/components/Layout/Menu/TopBar/Breadcrumb/Title';
import { withAuthenticatedUserEntity } from 'labstep-web/containers/AuthenticatedUser';
import { withSidebar } from 'labstep-web/containers/Ui/Sidebar';
import { Icon } from 'labstep-web/core/Icon';
import { Logo } from 'labstep-web/core/Logo';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import React from 'react';
import type {
  IBreadcrumbRootProps,
  LayoutMenuTopBarBreadcrumbProps,
} from './types';

export const BreadcrumbRoot: React.FC<IBreadcrumbRootProps> = ({
  setSidebar,
}) => (
  <div
    onClick={(): void => {
      setSidebar(true, 'left');
    }}
  >
    <Logo centered simplified />
  </div>
);

const BreadcrumbRootWrapper = withSidebar(BreadcrumbRoot);

export const LayoutMenuTopBarBreadcrumb: React.FC<
  LayoutMenuTopBarBreadcrumbProps
> = ({ breadcrumb = {}, authenticatedUserEntity }) => {
  const { organization, group, module, title } = breadcrumb;
  const { activeGroup } = useActiveGroup();

  const breadcrumbElements: Record<string, React.ReactNode> = {
    root: <BreadcrumbRootWrapper key="root" />,
  };

  if (organization && authenticatedUserEntity.userOrganization) {
    breadcrumbElements.organization = (
      <OrganizationContext
        organization={
          authenticatedUserEntity.userOrganization.organization
        }
        truncate={!!group || !!module}
      />
    );
  }

  if (group && activeGroup) {
    breadcrumbElements.group = (
      <GroupContext activeGroup={activeGroup} />
    );
  }

  if (group && module && activeGroup) {
    breadcrumbElements.module = (
      <ModuleContext
        moduleContext={module}
        activeGroup={activeGroup}
      />
    );
  }

  if (title) {
    breadcrumbElements.title = <Title key="title" title={title} />;
  }

  const breadcrumbElementsKeys = Object.keys(breadcrumbElements);
  const lastItemIndex =
    breadcrumbElementsKeys[breadcrumbElementsKeys.length - 1];

  return (
    <>
      {Object.keys(breadcrumbElements).map((index) => (
        <React.Fragment key={index}>
          {breadcrumbElements[index]}
          {index !== lastItemIndex && <Icon name="chevron right" />}
        </React.Fragment>
      ))}
    </>
  );
};

export default withAuthenticatedUserEntity(
  LayoutMenuTopBarBreadcrumb,
);
